import {
  availableServices,
  providerOf,
  type Destination,
  type InstalledDestination,
  type ServiceId,
} from "./destinations";
import { getInstalls, setInstalls } from "./storage";
import { DESTINATIONS_BY_ID } from "../config/catalogue";
import {
  applyRecheck,
  dueForRecheck,
  installByCode,
  recheckInstall,
  withInstall,
  withoutInstall,
  type InstallResult,
} from "./installs";

/**
 * The destinations this session has, for the UI to ask.
 *
 * One cached list over `storage`, because "is transcription available?" is
 * asked by several screens on every render and must not be an IndexedDB read
 * each time. `load()` once at boot; everything else reads the cache.
 *
 * Deliberately NOT a replacement for `currentHost()` — that still decides the
 * branding, which is a property of the deploy. What moves here is the half
 * that used to be decided by hostname and never should have been: where a film
 * can go, and what the app can do for you.
 */

let cache: InstalledDestination[] | null = null;
const listeners = new Set<() => void>();

/** Read the installs in. Safe to call repeatedly; only the first one reads. */
export async function loadInstalls(): Promise<InstalledDestination[]> {
  if (cache) return cache;
  cache = await getInstalls();
  return cache;
}

/**
 * What is installed, right now, without waiting.
 *
 * An empty list before `loadInstalls()` has finished — which is correct rather
 * than merely convenient: at that moment the app genuinely does not know of
 * any destination, and a screen that renders "no destinations yet" for one
 * frame is better than one that blocks on a disk read.
 */
export function installedNow(): readonly InstalledDestination[] {
  return cache ?? [];
}

export function onInstallsChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function commit(next: InstalledDestination[]): Promise<void> {
  cache = next;
  await setInstalls(next);
  for (const listener of listeners) listener();
}

/** Redeem a code and keep what it returns. */
export async function install(code: string): Promise<InstallResult> {
  const result = await installByCode(code);
  if (!result.ok) return result;
  await commit(
    withInstall(await loadInstalls(), result.destination, code, Date.now()),
  );
  return result;
}

/**
 * Install the deploy this browser is standing on, if it is one we know.
 *
 * The bridge through the consolidation. Someone opening the app on
 * me.o-kb.com has been publishing to OKB for a year and has no code for it —
 * demanding one to keep doing what they were already doing would be the
 * upgrade breaking their app. So the host installs itself, once, marked
 * `host` so nothing ever re-checks or revokes it.
 *
 * Already installed by code? Left alone. A user who deliberately added this
 * destination keeps their code and their original install date, and the
 * ordering that decides which destination provides a shared service does not
 * shift under them at boot.
 */
export async function installCurrentHost(
  hostKey: string,
  now = Date.now(),
): Promise<void> {
  const destination = DESTINATIONS_BY_ID[hostKey];
  if (!destination) return;
  const installed = await loadInstalls();
  if (installed.some((entry) => entry.destination.id === destination.id)) return;
  await commit(withInstall(installed, destination, "", now, "host"));
}

/**
 * Forget a destination.
 *
 * It takes the code with it, and there is no account to get either back from,
 * so the caller must say so before asking — see the confirm in the UI.
 */
export async function uninstall(destinationId: string): Promise<void> {
  await commit(withoutInstall(await loadInstalls(), destinationId));
}

/**
 * Re-check anything old enough, in the background.
 *
 * Fire-and-forget at boot: nothing waits on it, nothing breaks if it never
 * finishes, and a destination stays usable throughout. The only outcome that
 * changes anything is the catalogue saying a code is gone.
 */
export async function recheckStale(now = Date.now()): Promise<void> {
  const stale = dueForRecheck(await loadInstalls(), now);
  for (const entry of stale) {
    const verdict = await recheckInstall(entry);
    // Re-read each time: a user may have installed or removed something while
    // these were in flight, and folding into a stale list would undo them.
    await commit(
      applyRecheck(await loadInstalls(), entry.destination.id, verdict, Date.now()),
    );
  }
}

/* ── what the app can do, from what is installed ───────────────────────── */

export function hasService(service: ServiceId): boolean {
  return availableServices(installedNow()).includes(service);
}

/** The destination a service runs against, for its origin and its name. */
export function serviceProvider(service: ServiceId): Destination | null {
  return providerOf(installedNow(), service);
}

/** Only an api-host can take a whole project rather than a finished film. */
export function projectCapableDestinations(): Destination[] {
  return installedNow()
    .map((entry) => entry.destination)
    .filter((destination) => destination.accepts.shape === "project");
}

/** Test seam: forget everything read so far. */
export function resetInstalls(): void {
  cache = null;
  listeners.clear();
}
